import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import {
    ClubNavItem,
    Search,
    SeasonNavItem
} from 'widgets/player-awards-tracker/js/components/Leaderboard/components/nav-items';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { BrownlowPredictor } from './BrownlowPredictor';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { getTranslation } from 'common/react/utils/translations';
import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { leaderboardSelector } from 'common/store/modules/player-awards/leaderboard/selectors/simple-selectors';
import { setLeaderboardStateItem } from 'common/store/modules/player-awards/leaderboard';
import { useHasChangedFromDefaults } from 'common/react/components/competition-nav/hooks/use-has-changed-from-defaults';
import { useResetToDefaults } from 'common/react/components/competition-nav/hooks/use-reset-to-defaults';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown">{children}</div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const BPLeaderboard = ({
    seasonsToShow,
    seasonId,
    compId,
    seasonPid,
    title
}) => {
    const classPrefix = 'brownlow-leaderboard-predictor brownlow-leaderboard';
    const windowSize = useWindowSizeV2();

    const nav = useSelector(navSelector);
    const dispatch = useDispatch();
    const options = useCompetitionNavigationOptions(nav);

    const leaderboardState = useSelector(leaderboardSelector);

    options.defaultSeason = seasonPid ?? seasonPid;
    options.seasonsToShow = seasonsToShow ?? seasonsToShow;

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    const leaderboardRef = useRef();

    useEffect(() => {
        dispatch(
            initialiseNav({
                initialValues: {
                    competitionId: parseInt(compId),
                    seasonId: parseInt(seasonId)
                }
            })
        );
    }, [dispatch]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: { ...nav, ...leaderboardState },
        options,
        filtersFields: ['season', 'team'],
        filtersRef: leaderboardRef
    });

    const getInitialSeasonId = () => {
        return options.defaultSeason
            ? options.seasons?.find(
                  (season) => season.providerId === options.defaultSeason
              )?.id
            : options.seasons?.[0]?.id;
    };

    const { hasChangedFromDefaults } = useHasChangedFromDefaults({
        state: { nav: nav, team: leaderboardState?.team },
        initialValues: {
            seasonId: getInitialSeasonId(),
            team: -1
        }
    });

    const resetToDefaults = useResetToDefaults({
        initialValues: {
            competitionId: compId,
            seasonId: getInitialSeasonId()
        },
        onAfterReset: () => {
            dispatch(
                setLeaderboardStateItem({
                    item: 'team',
                    value: {
                        id: -1
                    }
                })
            );
        }
    });

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const selectedSeasonPid = useMemo(() => {
        return options?.seasons?.find((season) => season.id === nav.season?.id)
            ?.providerId;
    }, [nav, options]);

    return (
        <div ref={leaderboardRef} className={classPrefix}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <div className="player-awards-wrapper">
                            <SeasonNavItem
                                classes="shrink bp-season-nav-item"
                                seasonsToShow={seasonsToShow}
                                seasonId={seasonId}
                                compId={compId}
                                title={title}
                            />
                            <ClubNavItem
                                title={title}
                                classes={'clubnavitem'}
                            />
                        </div>
                        {hasChangedFromDefaults && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <StatsSection.InfoButton
                                    tooltipContent="BPLeaderboardTooltip"
                                    className="p-none"
                                    seasonPid={selectedSeasonPid}
                                />
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults && showResponsiveItems ? (
                        <StatsSection.NavFilterItems hideReset>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}

                    <StatsSection.SettingsButton>
                        <div className={`${classPrefix}-nav__extras`}>
                            <Search />
                        </div>
                        <StatsSection.InfoButton
                            tooltipContent="BPLeaderboardTooltip"
                            className="p-none"
                            seasonPid={selectedSeasonPid}
                        />
                        <SettingsButton />
                    </StatsSection.SettingsButton>

                    {showResponsiveItems ? (
                        <StatsSection.SearchBarMobile>
                            <div className={`${classPrefix}-nav__extras`}>
                                <Search />
                            </div>
                        </StatsSection.SearchBarMobile>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <BrownlowPredictor seasonId={seasonId} />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

BPLeaderboard.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonId: PropTypes.string,
    compId: PropTypes.string,
    seasonPid: PropTypes.string,
    title: PropTypes.string
};
